import { useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import {
  Button,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@monksflow/monks-ui';
import { usePermissions } from '@/entities/user';
import { SparkCard, SparkSkeletonCard } from '@/entities/persona-spark';
import { EmptyState } from '@/shared/ui/EmptyState';
import type { AudienceSparksDeps } from './AudienceSparks.types';
import { useTranslation } from 'react-i18next';

const PAGE_SIZE = 2;

export const AudienceSparks = ({
  useSparks,
  audienceAgentId,
  onViewOpportunity,
  onStartChat,
}: AudienceSparksDeps) => {
  const { t } = useTranslation('springboard');

  const { canViewAudiences, canOpenTool } = usePermissions();
  const canAccess = canViewAudiences && canOpenTool(audienceAgentId);

  const { sparks, isLoading } = useSparks();

  const [page, setPage] = useState(0);
  const [direction, setDirection] = useState(1);
  const visibleSparks = sparks.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  const canGoPrev = page > 0;
  const canGoNext = (page + 1) * PAGE_SIZE < sparks.length;

  const paginate = (delta: 1 | -1) => {
    setDirection(delta);
    setPage((p) => p + delta);
  };

  if (!canAccess) return null;

  return (
    <section className="content-block gap-md" data-testid="home-audience-sparks">
      <div className="flex justify-between items-center">
        <h2 className="type-heading-sm leading-5 tracking-[-0.02em]">
          {t('dashboard.audienceSparks.sparksFromTheAudience')}
        </h2>
        <div className="flex gap-xs">
          <TooltipProvider delayDuration={300}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => paginate(-1)}
                  disabled={!canGoPrev}
                  aria-label={t('dashboard.audienceSparks.previousSparks')}
                >
                  <ChevronLeftIcon className="size-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                {t('dashboard.audienceSparks.previousSparks')}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <TooltipProvider delayDuration={300}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => paginate(1)}
                  disabled={!canGoNext}
                  aria-label={t('dashboard.audienceSparks.nextSparks')}
                >
                  <ChevronRightIcon className="size-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                {t('dashboard.audienceSparks.nextSparks')}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 gap-grid md:grid-cols-2">
          <SparkSkeletonCard />
          <SparkSkeletonCard />
        </div>
      ) : visibleSparks.length > 0 ? (
        <div className="overflow-hidden">
          <AnimatePresence mode="wait" initial={false} custom={direction}>
            <motion.div
              key={page}
              custom={direction}
              variants={{
                enter: (d: number) => ({ x: d * 40, opacity: 0 }),
                center: { x: 0, opacity: 1 },
                exit: (d: number) => ({ x: d * -40, opacity: 0 }),
              }}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.2, ease: 'easeInOut' }}
              className="grid grid-cols-1 gap-grid md:grid-cols-2"
            >
              {visibleSparks.map((spark) => (
                <SparkCard
                  key={`${spark.persona.id}-${spark.spark.substring(0, 20)}`}
                  spark={spark}
                  onViewOpportunity={() => onViewOpportunity(spark.opportunityId)}
                  onStartChat={onStartChat ? () => onStartChat(spark) : undefined}
                  noBorder
                />
              ))}
            </motion.div>
          </AnimatePresence>
        </div>
      ) : (
        <EmptyState
          title={t('dashboard.audienceSparks.sparksFromAudience')}
          description={t('dashboard.audienceSparks.contentComingSoon')}
        />
      )}
    </section>
  );
};
